import type { StyleFunctionProps } from "@chakra-ui/styled-system"
import { defineStyle } from "@chakra-ui/styled-system"
import { theme } from "@chakra-ui/theme"

import merge from "lodash/merge"

const variantSolid = defineStyle((props: StyleFunctionProps) => {
  const { colorScheme } = props

  const baseStyle = {
    color: "white",
    bg: `${colorScheme}.500`,
    _hover: {
      bg: `${colorScheme}.600`,
    },
    _focus: {
      bg: `${colorScheme}.600`,
    },
    _disabled: {
      bg: `neutrals.600`,
      color: "neutrals.50",
      _hover: {
        bg: `neutrals.600`,
      },
      _focus: {
        bg: "neutrals.700",
      },
      _active: {
        bg: `neutrals.600`,
      },
    },
  }

  switch (colorScheme) {
    case "primary":
      return {
        ...baseStyle,
        bg: "surface.action",
        _hover: {
          bg: "surface.actionHover",
          _disabled: { bg: baseStyle._disabled.bg },
        },
      }
    case "tertiary":
      return {
        ...baseStyle,
        border: "1px solid",
        borderColor: "neutrals.200",
        color: "neutrals.900",
      }
    case "danger":
      return {
        ...baseStyle,
        color: "error.500",
        bg: "pink.100",
        _hover: {
          bg: "pink.300",
        },
        _focus: {
          bg: "pink.300",
        },
        _active: {
          bg: "pink.300",
        },
      }
    case "white":
      return {
        ...baseStyle,
        color: "black",
        bg: "white",
        border: "1px solid",
        borderColor: "neutrals.300",
        _hover: {
          bg: "neutrals.50",
        },
        _focus: {
          bg: "neutrals.100",
        },
      }
    case "brand":
      return {
        ...baseStyle,
        color: "text.actionDark",
        bg: "surface.brand",
        _hover: {
          bg: "main.300",
        },
        _focus: {
          bg: "main.300",
        },
        _active: {
          bg: "main.300",
        },
      }
    case "highlight":
      return {
        ...baseStyle,
        color: "white",
        bg: "linear-gradient(to right, rgb(168, 85, 247), rgb(59, 130, 246))",
        _hover: {
          bg: "linear-gradient(to right, rgb(147, 51, 234), rgb(37, 99, 235))",
        },
        _focus: {
          bg: "linear-gradient(to right, rgb(147, 51, 234), rgb(37, 99, 235))",
        },
        _active: {
          bg: "linear-gradient(to right, rgb(126, 34, 206), rgb(29, 78, 216))",
        },
        _focusVisible: {
          boxShadow: "rgb(168, 85, 247) 0px 0px 0px 2px",
        },
      }
    default:
      return baseStyle
  }
})

const variantSemiRounded = defineStyle((props: StyleFunctionProps) =>
  merge({}, variantSolid(props), {
    borderRadius: "lg",
  }),
)

const variantGhost = defineStyle((props: StyleFunctionProps) => {
  const { colorScheme } = props

  const hoverFocusBg = (() => {
    if (colorScheme === "secondary") return "neutrals.200"
    if (colorScheme === "white") return "white"
    return `${colorScheme}.50`
  })()

  const activeBg = (() => {
    if (colorScheme === "secondary") return "neutrals.300"
    if (colorScheme === "white") return "white"
    return `${colorScheme}.50`
  })()

  return {
    _hover: {
      bg: hoverFocusBg,
    },
    _focus: {
      bg: hoverFocusBg,
    },
    _active: {
      bg: activeBg,
    },
  }
})

const variantOutline = defineStyle((props: StyleFunctionProps) => {
  const { colorScheme } = props

  return {
    ...(colorScheme === "white" && {
      bg: "white",
      color: "black",
    }),
    ...(colorScheme === "primary" && {
      bg: "white",
      color: "main.900",
    }),
    _focus: {
      bg: colorScheme === "white" ? "neutrals.100" : "white",
      boxShadow: `0 0 0 2px var(--chakra-colors-${colorScheme}-200)`,
    },
    _hover: {
      bg: colorScheme === "white" ? "neutrals.50" : "white",
      boxShadow: `0 0 0 1px var(--chakra-colors-${colorScheme}-600)`,
    },
    _active: {
      bg: colorScheme === "white" ? "neutrals.50" : "white",
      boxShadow: `0 0 0 2px var(--chakra-colors-${colorScheme}-700)`,
    },
  }
})

const variantUnstyled = {
  display: "inline-flex",
  color: "neutrals.800",
  border: "none",
  _hover: {
    bg: "none",
    color: "neutrals.900",
  },
  _focus: {
    bg: "none",
    color: "neutrals.900",
  },
  _active: {
    bg: "none",
  },
}

const variantLink = defineStyle((props: StyleFunctionProps) => {
  const variantLink = theme.components.Button.variants?.link?.(props)
  const { colorScheme } = props

  return {
    ...variantLink,
    ...(colorScheme === "primary" && {
      color: "text.action",
    }),
    ...(colorScheme === "white" && {
      color: "white",
    }),
    _focus: {
      textDecoration: "underline",
    },
  }
})

const variantLinkUnderlined = defineStyle((props: StyleFunctionProps) => {
  return {
    ...variantLink(props),
    fontWeight: 600,
    textDecoration: "underline",
    _hover: {
      textDecoration: "none",
    },
    _focus: {
      textDecoration: "none",
    },
  }
})

const variantDropdown = defineStyle((props: StyleFunctionProps) =>
  merge({}, variantSemiRounded(props), {
    fontWeight: 400,
    border: "1px solid",
    borderColor: "neutrals.300",
    textAlign: "left",
    px: 4,
  }),
)

const variantMainActionsListItem = defineStyle((props: StyleFunctionProps) => {
  const { colorScheme } = props

  return {
    justifyContent: "flex-start",
    fontWeight: 400,
    fontSize: 12,
    borderRadius: "none",
    color: colorScheme === "error" ? "error.500" : "neutrals.900",
    _hover: {
      bg: "neutrals.20",
    },
    _focus: {
      bg: "neutrals.20",
    },
    _active: {
      bg: "white",
    },
  }
})

const variantNeutral = defineStyle(() => {
  return {
    color: "neutrals.900",
    bg: "white",
    border: "1px solid",
    borderColor: "border.disabled",
    fontWeight: "500",
    _hover: {
      bg: "surface.neutral",
    },
    _focus: {
      bg: "white",
      borderColor: "primary.500",
      borderWidth: "2px",
      boxShadow: "0 0 0 3px rgba(33, 201, 150, 0.1)",
    },
    _active: {
      bg: "neutrals.100",
      borderColor: "neutrals.400",
    },
    _disabled: {
      bg: "neutrals.50",
      color: "neutrals.400",
      borderColor: "neutrals.200",
      cursor: "not-allowed",
      opacity: 0.6,
      _hover: {
        bg: "neutrals.50",
        borderColor: "neutrals.200",
      },
    },
  }
})

/**
 * Built-in behavior for "solid" bg color:
 * default: 500
 * hover: 600
 * active: 700
 * @see {@link https://github.com/chakra-ui/chakra-ui/blob/v2/packages/theme/src/components/button.ts}
 */
export const Button = {
  baseStyle: {
    fontWeight: 600,
    borderRadius: "full",
    color: "#ffffff",
    boxShadow: "none",
    outline: "none",
  },
  sizes: {
    sm: {
      fontSize: "sm",
      px: 4,
      py: 2,
      w: "fit-content",
    },
    md: {
      fontSize: 14,
      px: 6,
      py: 2,
    },
    lg: {
      fontSize: 14,
      py: "12px",
      px: "34px",
    },
    xl: {
      fontSize: 16,
      py: "12px",
      px: "57px",
    },
  },
  variants: {
    "semi-rounded": variantSemiRounded,
    ghost: variantGhost,
    outline: variantOutline,
    unstyled: variantUnstyled,
    solid: variantSolid,
    neutral: variantNeutral,
    link: variantLink,
    linkUnderlined: variantLinkUnderlined,
    dropdown: variantDropdown,
    mainActionsListItem: variantMainActionsListItem,
  },
  defaultProps: {
    size: "md",
    variant: "solid",
    colorScheme: "primary",
  },
}
